import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache for 5 minutes
      staleTime: 1000 * 60 * 5,
      // Keep in cache for 10 minutes
      gcTime: 1000 * 60 * 10,
      // Retry failed requests
      retry: (failureCount, error: any) => {
        // Don't retry for 4xx errors except 408, 429
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          if (error?.response?.status === 408 || error?.response?.status === 429) {
            return failureCount < 2;
          }
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      // Exponential backoff
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Background refetch options
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: 'always',
      // Network mode
      networkMode: 'online',
    },
    mutations: {
      // Retry mutations once
      retry: 1,
      // Network mode for mutations
      networkMode: 'online',
    },
  },
});

// Query keys factory for consistency
export const queryKeys = {
  // Auth
  auth: ['auth'] as const,
  currentUser: () => [...queryKeys.auth, 'currentUser'] as const,
  
  // Clinic
  clinics: ['clinics'] as const,
  clinic: (id: string) => [...queryKeys.clinics, id] as const,
  clinicPatients: (clinicId: string) => [...queryKeys.clinics, clinicId, 'patients'] as const,
  
  // Patients
  patients: ['patients'] as const,
  patient: (id: string) => [...queryKeys.patients, id] as const,
  patientPrescriptions: (patientId: string) => [...queryKeys.patients, patientId, 'prescriptions'] as const,
  
  // Practitioners
  practitioners: ['practitioners'] as const,
  practitioner: (id: string) => [...queryKeys.practitioners, id] as const,
  
  // Programs & Products
  programs: ['programs'] as const,
  program: (id: string) => [...queryKeys.programs, id] as const,
  programDetails: (id: string, clinicId?: string) => {
    const key = [...queryKeys.programs, id, 'details'];
    return clinicId ? [...key, clinicId] : key;
  },
  
  products: ['products'] as const,
  product: (id: string) => [...queryKeys.products, id] as const,
  
  // Storefront
  storefront: ['storefront'] as const,
  storefrontPrograms: (clinicId?: string) => {
    const key = [...queryKeys.storefront, 'programs'];
    return clinicId ? [...key, clinicId] : key;
  },
  
  // Prescriptions
  prescriptions: ['prescriptions'] as const,
  prescription: (id: string) => [...queryKeys.prescriptions, id] as const,
  
  // Payments
  payments: ['payments'] as const,
  paymentAccount: (clinicId: string) => [...queryKeys.payments, 'account', clinicId] as const,
  
  // Medication Templates
  medicationTemplates: ['medicationTemplates'] as const,
  practitionerMedicationTemplates: (practitionerId: string, patientId?: string) =>
    [...queryKeys.medicationTemplates, 'practitioner', practitionerId, patientId || 'global'] as const,
} as const;

// Prefetch utilities
export const prefetchQueries = {
  // Prefetch critical routes
  async prefetchStorefront(clinicId?: string) {
    return queryClient.prefetchQuery({
      queryKey: queryKeys.storefrontPrograms(clinicId),
      queryFn: async () => {
        const { getPrograms } = await import('@/services/storefrontService');
        return getPrograms({ clinicId });
      },
      staleTime: 1000 * 60 * 10, // 10 minutes
    });
  },

  async prefetchProgramDetails(programId: string, clinicId?: string) {
    return queryClient.prefetchQuery({
      queryKey: queryKeys.programDetails(programId, clinicId),
      queryFn: async () => {
        const { getProgramDetails } = await import('@/services/storefrontService');
        return getProgramDetails(programId, clinicId);
      },
      staleTime: 1000 * 60 * 15, // 15 minutes
    });
  },

  async prefetchCurrentUser() {
    return queryClient.prefetchQuery({
      queryKey: queryKeys.currentUser(),
      queryFn: async () => {
        const { authService } = await import('@/services/authService');
        return authService.getCurrentUser();
      },
      staleTime: 1000 * 60 * 30, // 30 minutes
    });
  },
};

// Cache management utilities
export const cacheUtils = {
  // Invalidate related queries
  invalidateAuth() {
    queryClient.invalidateQueries({ queryKey: queryKeys.auth });
  },
  
  invalidateClinicData(clinicId: string) {
    queryClient.invalidateQueries({ queryKey: queryKeys.clinic(clinicId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.clinicPatients(clinicId) });
  },
  
  invalidatePatientData(patientId: string) {
    queryClient.invalidateQueries({ queryKey: queryKeys.patient(patientId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.patientPrescriptions(patientId) });
  },
  
  // Clear all caches (useful for logout)
  clearAll() {
    queryClient.clear();
  },
  
  // Remove specific queries
  removeQuery(queryKey: readonly unknown[]) {
    queryClient.removeQueries({ queryKey });
  },
}; 